import type { FunctionComponent } from "react";
import { useCallback, useMemo, useState } from "react";

import TableHeader from "~/components/Table/components/TableHeader";
import TableHeaderCell from "~/components/Table/components/TableHeaderCell";
import TableWrapper from "~/components/Table/components/TableWrapper";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import type { ProjectRevenueReport } from "~/modules/project/api/projectRevenueReport/projectRevenueReportTypes.ts";
import ProjectRevenueMonthRow
	from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/components/ProjectRevenueMonthRow/ProjectRevenueMonthRow.tsx";
import { buildTimelineRows } from "~/modules/project/components/ProjectDetailsView/components/ProjectRevenueReportSection/projectRevenueReportUtils.ts";

type Props = {
	report: ProjectRevenueReport;
	phasesById: Record<string, ProjectPhase>;
	ordersById: Record<string, OrderWithBillingSettings>;
	invoicesById: Record<string, Invoice>;
};

const ProjectRevenueTimeline: FunctionComponent<Props> = ({
	report,
	phasesById,
	ordersById,
	invoicesById,
}) => {
	const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

	const rows = useMemo(() => buildTimelineRows(report), [report]);

	const handleToggle = useCallback((month: string) => {
		setExpandedMonths((previous) => {
			const next = new Set(previous);
			if (next.has(month)) {
				next.delete(month);
			} else {
				next.add(month);
			}
			return next;
		});
	}, []);

	return <div className="size-full overflow-y-auto">
		<TableWrapper rounded={true}>
			<TableHeader>
				<tr>
					<TableHeaderCell>Monat</TableHeaderCell>
					<TableHeaderCell textAlign="right">Abrechenbar</TableHeaderCell>
					<TableHeaderCell textAlign="right">Nicht abrechenbar</TableHeaderCell>
					<TableHeaderCell>Rechnungen</TableHeaderCell>
				</tr>
			</TableHeader>
			{rows.map((row) => <ProjectRevenueMonthRow key={row.month}
													   row={row}
													   phasesById={phasesById}
													   ordersById={ordersById}
													   invoicesById={invoicesById}
													   isExpanded={expandedMonths.has(row.month)}
													   onToggle={handleToggle} />)}
		</TableWrapper>
	</div>;
};

export default ProjectRevenueTimeline;
